/**
 * Narrowing helpers for the two DTO fields that are deliberately `unknown`:
 * `ProfileSnapshotDTO.full` (`full_json`) and `.positions` (`positions_json`), plus
 * `PostDetailDTO.extras`.
 *
 * The mapper preserves whatever the actor sent — including keys nobody has seen yet (§7.4) — so
 * Profile mode renders "whatever the scraper provides" (PRD). These readers make that render
 * total: an absent or wrongly-shaped branch yields `undefined` and the section simply does not
 * appear. No value is ever coerced, invented or computed.
 */

export type UnknownRecord = Record<string, unknown>;

export function asRecord(value: unknown): UnknownRecord | undefined {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as UnknownRecord)
    : undefined;
}

export function asArray(value: unknown): unknown[] | undefined {
  return Array.isArray(value) ? value : undefined;
}

export function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

export function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** Reads `record[key]` as a record. */
export function recordAt(value: unknown, key: string): UnknownRecord | undefined {
  return asRecord(asRecord(value)?.[key]);
}

/** Reads `record[key]` as an array of records (skipping entries that are not records). */
export function recordsAt(value: unknown, key: string): UnknownRecord[] {
  const list = asArray(asRecord(value)?.[key]) ?? [];
  return list.map((entry) => asRecord(entry)).filter((entry): entry is UnknownRecord => entry !== undefined);
}

/** Reads `record[key]` as a list of non-empty strings, tolerating `{name}`/`{title}` objects. */
export function stringsAt(value: unknown, key: string): string[] {
  const list = asArray(asRecord(value)?.[key]) ?? [];
  return list
    .map((entry) => {
      const direct = asString(entry);
      if (direct !== undefined) return direct;
      const record = asRecord(entry);
      if (record === undefined) return undefined;
      return asString(record.name) ?? asString(record.title) ?? asString(record.text);
    })
    .filter((entry): entry is string => entry !== undefined);
}

/** First present string among `keys` — used to label heterogeneous section entries. */
export function firstString(record: UnknownRecord | undefined, ...keys: string[]): string | undefined {
  if (record === undefined) return undefined;
  for (const key of keys) {
    const found = asString(record[key]);
    if (found !== undefined) return found;
  }
  return undefined;
}

/**
 * `{month,year,text}` as the mapper stores it — rendered verbatim, never parsed into a date
 * (the actor's own display string is the only trustworthy form).
 */
export function periodText(record: UnknownRecord | undefined, key: string): string | undefined {
  const date = asRecord(record?.[key]);
  if (date === undefined) return asString(record?.[key]);
  const text = asString(date.text);
  if (text !== undefined) return text;
  const month = asString(date.month) ?? (asNumber(date.month) !== undefined ? String(date.month) : undefined);
  const year = asNumber(date.year) !== undefined ? String(date.year) : asString(date.year);
  return [month, year].filter((part): part is string => part !== undefined).join(' ') || undefined;
}
